import { useRef, useState } from 'react';
import { downloadAuthenticatedFile } from '../services/apiClient';
import { deleteStudentAnswerFile, uploadStudentAnswerFile } from '../services/submissionAnswerService';
import {
  formatAttachmentSize,
  getAttachmentName,
  getAttachmentUrl,
} from '../utils/fileAttachmentUtils';

function AnswerFileUpload({
  disabled = false,
  entityId,
  file,
  onFileChange,
  questionId,
  slug,
  title = 'فایل پاسخ',
}) {
  const inputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  async function handleFileSelect(event) {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    setIsUploading(true);
    setErrorMessage('');

    try {
      const result = await uploadStudentAnswerFile(slug, entityId, questionId, selectedFile);
      onFileChange?.(result?.file || null);
    } catch (error) {
      setErrorMessage(error.message || 'بارگذاری فایل انجام نشد.');
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  }

  async function handleDeleteFile() {
    setIsDeleting(true);
    setErrorMessage('');

    try {
      await deleteStudentAnswerFile(slug, entityId, questionId);
      onFileChange?.(null);
    } catch (error) {
      setErrorMessage(error.message || 'حذف فایل انجام نشد.');
    } finally {
      setIsDeleting(false);
    }
  }

  const fileName = file ? getAttachmentName(file) : '';
  const fileSize = file ? formatAttachmentSize(file.size) : '';
  const fileUrl = file ? getAttachmentUrl(file) : '';
  const isBusy = isUploading || isDeleting;

  async function handleDownloadFile() {
    if (!fileUrl) {
      return;
    }

    try {
      await downloadAuthenticatedFile(fileUrl, fileName);
    } catch (error) {
      setErrorMessage(error.message || 'دریافت فایل انجام نشد.');
    }
  }

  return (
    <div className="rounded-[8px] border border-gray-200 bg-gray-50 p-3 text-slate-800">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-bold">{title}</p>
        {!disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isBusy}
            className="rounded-[5px] bg-white px-3 py-1 text-[10px] font-bold text-slate-700 shadow-sm disabled:opacity-60"
          >
            {isUploading ? 'در حال بارگذاری...' : file ? 'تغییر فایل' : 'انتخاب فایل'}
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
        disabled={disabled || isBusy}
      />

      {file ? (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-[6px] bg-[#F8F8F8] px-3 py-2">
          <div className="min-w-0">
            {fileUrl ? (
              <button
                type="button"
                onClick={handleDownloadFile}
                className="block truncate text-right text-[11px] font-semibold text-[#2563EB] underline"
              >
                {fileName}
              </button>
            ) : (
              <p className="truncate text-[11px] font-semibold">{fileName}</p>
            )}
            {fileSize && <p className="text-[10px] text-slate-500">{fileSize}</p>}
          </div>

          {!disabled && (
            <button
              type="button"
              onClick={handleDeleteFile}
              disabled={isBusy}
              className="shrink-0 rounded-[5px] bg-[#FEE2E2] px-2 py-1 text-[10px] font-bold text-[#DC2626] disabled:opacity-60"
            >
              {isDeleting ? '...' : 'حذف'}
            </button>
          )}
        </div>
      ) : (
        <p className="mt-2 text-[10px] text-slate-500">
          {disabled ? 'فایلی برای این پاسخ ثبت نشده است.' : 'می‌توانی فایل پاسخ را اینجا بارگذاری کنی.'}
        </p>
      )}

      {errorMessage && <p className="mt-2 text-[10px] font-bold text-[#DC2626]">{errorMessage}</p>}
    </div>
  );
}

export default AnswerFileUpload;
